/**
 * Ingest → workers contract + pure enrichment helpers (Palier 1C).
 *
 * The ingest service validates a beacon, resolves its project and enqueues a
 * `BeaconJob` on the BullMQ `beacons` queue. The workers consume the queue,
 * enrich the data (UA parse lives in the worker — it needs a heavy dep) and
 * batch-insert into ClickHouse. Everything here is dependency-free and unit
 * tested so both sides agree on the shapes and the classification rules.
 */

import type { Beacon } from './beacon';

/** BullMQ queue name shared by the ingest (producer) and workers (consumer). */
export const BEACON_QUEUE = 'beacons';

/** Structural Redis connection options (assignable to BullMQ's ConnectionOptions). */
export interface RedisConnectionOptions {
  host: string;
  port: number;
  username?: string;
  password?: string;
  db?: number;
}

/** Parse a `redis://[:pass@]host:port[/db]` URL into connection options. */
export function parseRedisConnection(redisUrl: string): RedisConnectionOptions {
  const u = new URL(redisUrl);
  return {
    host: u.hostname,
    port: u.port ? Number(u.port) : 6379,
    username: u.username ? decodeURIComponent(u.username) : undefined,
    password: u.password ? decodeURIComponent(u.password) : undefined,
    db: u.pathname.length > 1 ? Number(u.pathname.slice(1)) : undefined,
  };
}

/** ClickHouse `source_type` enum domain. */
export type SourceType =
  | 'ads'
  | 'seo'
  | 'direct'
  | 'referral'
  | 'social'
  | 'internal'
  | 'bot'
  | 'spam'
  | 'unknown';

/** ClickHouse `bot_category` enum domain (populated in 1D). */
export type BotCategory =
  | ''
  | 'search'
  | 'seo'
  | 'ai'
  | 'social'
  | 'monitoring'
  | 'malicious'
  | 'unknown';

/** Unit of work enqueued by the ingest and consumed by a worker. */
export interface BeaconJob {
  beacon: Beacon;
  /** Project resolved from the beacon public key (`k`). */
  projectId: string;
  /** Client IP captured by the ingest (Fastify trustProxy). */
  ip: string;
  /** Raw User-Agent header. */
  userAgent: string;
  /** Raw Accept-Language header (extended fingerprint, 2A). */
  acceptLanguage: string;
  /** TLS/HTTP2 fingerprints injected by fingerproxy (2B.b, SCORING-SPEC §6bis).
   *  '' when fingerproxy is not in the path or the headers aren't trusted. */
  tlsJa4: string;
  tlsJa3: string;
  tlsHttp2: string;
  /** Server receive time, epoch milliseconds. */
  receivedAt: number;
  /** Project setting: store only the /24 block instead of the full IP. */
  anonymizeIp: boolean;
}

// --- Tracker connection verification (server-issued headless visit) ---------

/**
 * Query param the verification visit appends to the client's URL. The tracker
 * echoes the page url inside the beacon, so the ingest recognises the verification
 * hit and FULLY EXCLUDES it from analytics, visit counts, geo/audience and fraud
 * scoring — it only flips the "connection verified" indicator. The nonce is
 * server-issued (one-time, Redis), so a real visit can never be mistaken for one.
 */
export const TRACKER_VERIFY_PARAM = '__ss_verify';

/** Extract the verification nonce from a beacon page url, if present. Pure + safe. */
export function extractVerifyNonce(url: string | undefined | null): string | null {
  if (!url || !url.includes(TRACKER_VERIFY_PARAM)) return null;
  const q = url.indexOf('?');
  if (q < 0) return null;
  const value = new URLSearchParams(url.slice(q + 1)).get(TRACKER_VERIFY_PARAM);
  return value && /^[a-f0-9]{16,64}$/i.test(value) ? value : null;
}

/** Page url (pageview/event) or landing page (session) carried by a beacon. */
export function beaconPageUrl(beacon: Beacon): string {
  const d = beacon.d as { url?: string; landingPage?: string };
  return d.url ?? d.landingPage ?? '';
}

/** BullMQ queue for the server-side headless tracker-verification visit. */
export const TRACKER_VERIFY_QUEUE = 'tracker-verify';

/** Job: load the client's page headlessly so the tracker fires a marked beacon. */
export interface TrackerVerifyJob {
  projectId: string;
  /** Full page URL to visit, including `?__ss_verify=<nonce>`. */
  url: string;
  nonce: string;
}

/** BullMQ queue for the end-of-grace project purge sweep (consumed by apps/workers). */
export const PROJECT_PURGE_QUEUE = 'project-purge';

// --- Source classification -------------------------------------------------

const SEARCH_ENGINES = [
  'google',
  'bing',
  'yahoo',
  'duckduckgo',
  'ecosia',
  'baidu',
  'yandex',
  'qwant',
  'startpage',
  'brave',
];

const SOCIAL_DOMAINS = [
  'facebook.com',
  'instagram.com',
  'twitter.com',
  'x.com',
  't.co',
  'linkedin.com',
  'lnkd.in',
  'tiktok.com',
  'youtube.com',
  'youtu.be',
  'reddit.com',
  'pinterest.com',
  'snapchat.com',
  'whatsapp.com',
  'telegram.org',
  't.me',
];

const PAID_MEDIUMS = new Set([
  'cpc',
  'ppc',
  'paid',
  'paidsearch',
  'paid-search',
  'paid_social',
  'display',
  'cpm',
  'banner',
  'retargeting',
]);

/** Bare lowercase hostname of a URL (leading `www.` stripped). '' if unparsable. */
export function hostOf(url: string): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** Registrable-ish domain of a referrer (currently the bare host). */
export function referrerDomain(referrer: string): string {
  return hostOf(referrer);
}

function isSearchEngine(host: string): boolean {
  if (!host) return false;
  const label = host.split('.')[0] ?? '';
  return SEARCH_ENGINES.includes(label) || SEARCH_ENGINES.some((e) => host.includes(`${e}.`));
}

function isSocial(host: string): boolean {
  return SOCIAL_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));
}

const GAD_CAMPAIGN_RE = /[?&]gad_campaignid=(\d+)/;
/**
 * Effective ads campaign id for a visit. Manual URL tagging carries `utm_campaign`;
 * Google auto-tagging omits it and instead adds `gad_campaignid` to the landing URL.
 * Fall back to that so auto-tagged clicks are attributed to their campaign, not lost.
 */
export function adsCampaignId(utmCampaign: string, url: string): string {
  if (utmCampaign) return utmCampaign;
  return url.match(GAD_CAMPAIGN_RE)?.[1] ?? '';
}

export interface SourceSignals {
  referrer: string;
  /** Host of the landing page — lets us tell internal navigation from referral. */
  landingHost: string;
  utmMedium: string;
  utmSource: string;
  gclid: string;
  gbraid: string;
  wbraid: string;
}

/**
 * Classify a visit's traffic source. P1 covers ads/seo/social/referral/internal/
 * direct; `bot`/`spam`/`unknown` are reserved for later paliers.
 */
export function classifySource(s: SourceSignals): SourceType {
  if (s.gclid || s.gbraid || s.wbraid) return 'ads';
  const medium = s.utmMedium.toLowerCase();
  if (PAID_MEDIUMS.has(medium)) return 'ads';

  const refHost = hostOf(s.referrer);
  if (medium === 'social' || isSocial(refHost)) return 'social';

  if (!s.referrer) {
    return s.utmSource || s.utmMedium ? 'referral' : 'direct';
  }
  if (s.landingHost && refHost === s.landingHost) return 'internal';
  if (isSearchEngine(refHost)) return 'seo';
  return 'referral';
}

// --- IP anonymisation ------------------------------------------------------

export interface IpParts {
  ipV4: string;
  ipV6: string;
  ip24: string;
  ip16: string;
}

const ZERO_IP: IpParts = { ipV4: '0.0.0.0', ipV6: '::', ip24: '', ip16: '' };

/**
 * Split a client IP into the columns the `visits` table needs. When `anonymize`
 * is set, the stored full address is reduced to its /24 (v4) or /64 (v6) block.
 */
/**
 * Expand an IPv6 address into its 8 hextets (lowercase, zero-compression and an
 * embedded IPv4 tail resolved, zone index stripped). Returns null when invalid.
 */
function expandIpv6(input: string): string[] | null {
  let a = input.toLowerCase();
  const zone = a.indexOf('%');
  if (zone >= 0) a = a.slice(0, zone);
  const v4tail = a.match(/^(.*:)(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (v4tail?.[1]) {
    const octets = [v4tail[2], v4tail[3], v4tail[4], v4tail[5]].map(Number);
    if (octets.some((n) => n > 255)) return null;
    const hi = ((octets[0] as number) * 256 + (octets[1] as number)).toString(16);
    const lo = ((octets[2] as number) * 256 + (octets[3] as number)).toString(16);
    a = `${v4tail[1]}${hi}:${lo}`;
  }
  const halves = a.split('::');
  if (halves.length > 2) return null;
  const head = halves[0] ? halves[0].split(':') : [];
  const tail = halves.length === 2 && halves[1] ? halves[1].split(':') : [];
  if (halves.length === 1 && head.length !== 8) return null;
  const missing = 8 - head.length - tail.length;
  if (halves.length === 2 && missing < 1) return null;
  const groups = [...head, ...Array(halves.length === 2 ? missing : 0).fill('0'), ...tail];
  if (groups.length !== 8 || !groups.every((g) => /^[0-9a-f]{1,4}$/.test(g))) return null;
  return groups.map((g) => g.replace(/^0+(?=.)/, ''));
}

export function ipParts(ip: string, anonymize: boolean): IpParts {
  if (!ip) return { ...ZERO_IP };
  let addr = ip.trim();
  const mapped = addr.match(/^::ffff:(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/i);
  if (mapped?.[1]) addr = mapped[1];

  const v4 = addr.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (v4) {
    const [, a, b, c, d] = v4;
    const ip24 = `${a}.${b}.${c}.0`;
    const ip16 = `${a}.${b}.0.0`;
    return { ipV4: anonymize ? ip24 : `${a}.${b}.${c}.${d}`, ipV6: '::', ip24, ip16 };
  }

  if (addr.includes(':')) {
    const groups = expandIpv6(addr);
    if (!groups) return { ...ZERO_IP };
    // IPv6 analogues of the v4 groupings: the /64 is what an ISP assigns to ONE
    // household/subscription (addresses rotate freely inside it) so it plays the
    // role of the exact-IP/24 key, and the /48 is the pool analogue of the /16.
    // Downstream groupings (recidivism keys, pool detector) work unchanged.
    const ip64 = `${groups.slice(0, 4).join(':')}::`;
    const ip48 = `${groups.slice(0, 3).join(':')}::`;
    return { ipV4: '0.0.0.0', ipV6: anonymize ? ip64 : addr, ip24: ip64, ip16: ip48 };
  }

  return { ...ZERO_IP };
}

// --- ClickHouse datetime ---------------------------------------------------

/** Format epoch ms as a ClickHouse `DateTime64(3)` literal in UTC. */
export function chDateTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number, len = 2): string => String(n).padStart(len, '0');
  return (
    `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ` +
    `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}.${p(d.getUTCMilliseconds(), 3)}`
  );
}
